import {ApplicationRef, Injectable} from "@angular/core";
import "rxjs/operator/map";
import "rxjs/add/observable/fromEvent";
import {Observable} from "rxjs/Observable";
import {Subject} from "rxjs/Subject";
import {BehaviorSubject, ReplaySubject} from "rxjs";
import {Platform} from "ionic-angular";
import {LocalConfigurationService} from "@fedelta-pos/data-client";

export enum PageMode {
    SMALL_MOBILE,
    MEDIUM_MOBILE,
    MOBILE,
    NARROW_DESKTOP,
    BASIC_DESKTOP,
    WIDE_DESKTOP,
    WIDER_DESKTOP,
    WIDEST_DESKTOP,
}

@Injectable()
export class ResponsiveService {
    public isApp: boolean;
    public isCordova: boolean;
    public isIos: boolean;
    public isIpad: boolean;
    public isAndroid: boolean;
    public isMobile: boolean;
    public isSafari: boolean;
    public isChrome: boolean;
    public isIOSChrome: boolean;
    public isEdge: boolean;
    public isFirefox: boolean;
    public hasViewport: boolean;
    public forceMobileParam: boolean;
    public isTouchDevice: boolean;
    public isKeyboardOpen: boolean = false;
    public isDocumentElementReducedForKeyboard: boolean = false;
    private _loaded$: Subject<Window> = new Subject<Window>();
    // noinspection JSUnusedGlobalSymbols
    public loaded$: Observable<Window> = this._loaded$.asObservable();
    private _window$: BehaviorSubject<Window> = new BehaviorSubject<Window>(window);
    // noinspection JSUnusedGlobalSymbols
    public window$: Observable<Window> = this._window$.asObservable();
    // fires only when the width has changed
    private _windowWidth$: Subject<Window> = new Subject<Window>();
    public windowWidth$: Observable<Window> = this._windowWidth$.asObservable();
    // fires only when the height has changed
    private _windowHeight$: Subject<Window> = new Subject<Window>();
    public windowHeight$: Observable<Window> = this._windowHeight$.asObservable();
    // fires only when the mode has changed
    private _mode$: Subject<PageMode> = new ReplaySubject<PageMode>(1);
    public mode$: Observable<PageMode> = this._mode$.asObservable();
    private _mobile$: Subject<boolean> = new ReplaySubject<boolean>(1);
    public mobile$: Observable<boolean> = this._mobile$.asObservable();
    private _desktop$: Subject<boolean> = new ReplaySubject<boolean>(1);
    public desktop$: Observable<boolean> = this._desktop$.asObservable();
    private lastViewportWidth: number;
    private lastViewportHeight: number;
    private lastWindowWidth: number;
    private lastWindowHeight: number;
    private _keyboard$: Subject<boolean> = new ReplaySubject<boolean>(1);
    // noinspection JSUnusedGlobalSymbols
    public keyboard$: Observable<boolean> = this._keyboard$.asObservable();

    constructor(private applicationRef: ApplicationRef,
                public localConfigurationService: LocalConfigurationService,
                private platform: Platform) {
        console.log("--- ResponsiveService");

        this.isCordova = !!window["cordova"];
        this.isIpad = (/iPad/.test(navigator.userAgent) && !window["MSStream"]) || (/Mac/.test(navigator.userAgent) && navigator.maxTouchPoints && navigator.maxTouchPoints > 2);
        this.isIos = this.platform.is("ios") || (/iPhone|iPod/.test(navigator.userAgent) && !window["MSStream"]) || this.isIpad || (this.isCordova && window["cordova"]["platformId"] == "ios");
        this.isAndroid = this.platform.is("android") || /android/i.test(navigator.userAgent) || (this.isCordova && window["cordova"]["platformId"] == "android");
        this.isApp = this.platform.is("cordova");
        this.isMobile = this.isIos || this.isAndroid;
        this.isSafari = /Safari/.test(navigator.userAgent) && /Apple Computer/.test(navigator.vendor);
        this.isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
        this.isIOSChrome = /CriOS/.test(navigator.userAgent);
        this.isEdge = /Edge/.test(navigator.userAgent);
        this.isFirefox = /Firefox/i.test(navigator.userAgent);
        this.hasViewport = window["visualViewport"];
        this.forceMobileParam = new URLSearchParams(window.location.search).has("forceMobile");
        this.isTouchDevice = (("ontouchstart" in window) || (navigator.maxTouchPoints > 0) || (navigator.msMaxTouchPoints > 0));

        // noinspection SpellCheckingInspection
        document.addEventListener("deviceready", () => this.manageOrientation());

        this.lastWindowWidth = this.windowWidth;
        this.lastWindowHeight = this.windowHeight;
        this._mode = this.calculateMode(this.lastWindowWidth);
        this._short = this.availableHeight < 768;
        this.notifyMode();

        /**
         * Watch the window for resize
         */
        Observable.fromEvent(window, "resize")
            .debounceTime(1)
            .subscribe(() => this.handleWindowResize(false, true));

        if (window["visualViewport"]) {
            Observable.fromEvent(window["visualViewport"], "resize")
                .debounceTime(1)
                .subscribe(() => this.handleViewportResize());
        }

        /**
         * Watch the window for load
         */
        Observable.fromEvent(window, "load")
            .debounceTime(1)
            .subscribe(() => {
                    this._loaded$.next(window);

                    /**
                     * We have to tell the application something has changed
                     * because this is not part of the change detection that
                     * Angular runs
                     */
                    this.applicationRef.tick();
                }
            );

        Observable.fromEvent(document, "keydown")
            .subscribe((event: KeyboardEvent) => {
                if (event.shiftKey && event.ctrlKey && event.code === "KeyK") {
                    if (this.isKeyboardOpen) {
                        this.notifyKeyboardClose();
                    } else {
                        this.notifyKeyboardOpen(398, true);
                    }
                }
            });
    }

    private _mode: PageMode;

    public get mode(): PageMode {
        return this._mode;
    }

    private _short: boolean;

    public get short(): boolean {
        return this._short;
    }

    public get mobile(): boolean {
        return this._mode === PageMode.MOBILE || this._mode === PageMode.MEDIUM_MOBILE || this._mode === PageMode.SMALL_MOBILE;
    }

    public get desktop(): boolean {
        return !this.mobile;
    }

    public get windowWidth(): number {
        // Android chrome viewport sizing now matches safari
        return /* this.isSafari && this.isIos && */ this.hasViewport ? Math.floor(this.viewportWidth) : window.innerWidth;
    }

    public get windowHeight(): number {
        // Android chrome viewport sizing now matches safari
        return /* this.isSafari && this.isIos && */ this.hasViewport ? Math.floor(this.viewportHeight) : window.innerHeight;
    }

    public get viewportWidth(): number {
        return this.hasViewport ? window["visualViewport"].width : undefined;
    }

    public get viewportHeight(): number {
        return this.hasViewport ? window["visualViewport"].height : undefined;
    }

    public get availableHeight(): number {
        return this.isDocumentElementReducedForKeyboard || (this.isIos && this.isKeyboardOpen) ? document.documentElement.offsetHeight : this.windowHeight;
    }

    public get window(): Window {
        return window;
    }

    /**
     * Determines the actual width available considering the mobile/desktop pillarbox width obtained from the css custom properties --woa-full-page-pillarbox-width or --woa-desktop-pillarbox-width
     *
     * @param nativeElement - used to obtain the css custom property.
     * @param pillarboxed - is the page currently pillarboxed? (if not, just use the window width).
     */
    public calculateAvailableWidth(nativeElement: any, pillarboxed: "mobile" | "desktop" | "double-mobile" | "none"): number {
        if (!pillarboxed || pillarboxed === "none") {
            return this.windowWidth;
        }
        const pillarboxWidthProperty: string = pillarboxed === "desktop" ? "--woa-desktop-pillarbox-width" : "--woa-full-page-pillarbox-width";
        let pillarboxWidth: number = parseInt(getComputedStyle(nativeElement).getPropertyValue(pillarboxWidthProperty).replace("px", ""));
        if (pillarboxed === "double-mobile") {
            pillarboxWidth *= 2;
        }
        return Math.min(pillarboxWidth, this.windowWidth);
    }

    /**
     * Determines the responsive page mode.
     *
     * The check for MOBILE vs (*_)DESKTOP mode is based on the actual window width.
     * Those breakpoints are calculated for a 1024x768 screen with sidebar-width allowance of up to 250 pixels, giving 774 pixels.
     * The check for (*_)DESKTOP variations is based on the provided desktopWidth which may have been adjusted for desktop pillar-boxing (see calculateAvailableWidth() above)
     *
     * @param desktopWidth - the width to use to determine which desktop mode to return (does not affect decision between mobile and desktop)
     */
    public calculateMode(desktopWidth: number): PageMode {
        // safari on ios < 13 does not report window resize when the keyboard displays.
        // This causes problems in desktop mode if the window is not sufficiently high.
        // So, for safari on ios < 13, we report MOBILE mode unless the window height at least 650;
        const forceMobile: boolean = this.isSafari && this.isIos && !this.hasViewport && window.innerHeight < 650 || this.forceMobileParam;

        return window.innerWidth < 375 ? PageMode.SMALL_MOBILE
            : window.innerWidth < 425 ? PageMode.MEDIUM_MOBILE
                : forceMobile || window.innerWidth < 774 ? PageMode.MOBILE
                    : desktopWidth < 1024 ? PageMode.NARROW_DESKTOP
                        : desktopWidth < 1440 ? PageMode.BASIC_DESKTOP
                            : desktopWidth < 1920 ? PageMode.WIDE_DESKTOP
                                : desktopWidth < 2560 ? PageMode.WIDER_DESKTOP
                                    : PageMode.WIDEST_DESKTOP;
    }

    private handleViewportResize(): void {
        console.log("Screen size: " + window.screen.width + "x" + window.screen.height);
        console.log("Viewport size: " + this.viewportWidth + "x" + this.viewportHeight);

        // Issue with opening new tabs on IOS safari and chrome:
        // Depending on the timing of opening the app, the initial viewport size reported may be the full screen size,
        // which is shortly thereafter changed to reflect the actual available viewport size taking into account browser
        // headers and footers.
        // This can cause a false positive for detecting a keyboard open as the viewport height reduces.
        // To solve this, if the initial viewport dimensions match the full screen size, we don't attempt to infer
        // that the keyboard has opened.
        let suppressKeyboardChange: boolean = false;
        if (!this.isApp && this.isMobile) {
            suppressKeyboardChange = this.lastViewportWidth !== this.viewportWidth;
            if (!suppressKeyboardChange &&
                Math.max(this.lastViewportWidth, this.lastViewportHeight) === Math.max(window.screen.width, window.screen.height) &&
                Math.min(this.lastViewportWidth, this.lastViewportHeight) === Math.min(window.screen.width, window.screen.height)) {
                console.log("Previous viewport size matched full screen - not inferring keyboard transition");
                suppressKeyboardChange = true;
            }
        }
        this.lastViewportWidth = this.viewportWidth;
        this.lastViewportHeight = this.viewportHeight;

        this.handleWindowResize(suppressKeyboardChange, true);
    }

    private handleWindowResize(suppressKeyboardChange: boolean, deferOnKeyboardChange: boolean): void {
        this._window$.next(window);

        /**
         * We have to tell the application something has changed
         * because this is not part of the change detection that
         * Angular runs
         */
        this.applicationRef.tick();

        if (this.lastWindowWidth !== this.windowWidth || this.lastWindowHeight !== this.windowHeight) {
            console.log("Window size: " + this.windowWidth + "x" + this.windowHeight);
            let notifyHeight: boolean = false;
            let notifyWidth: boolean = false;

            if (this.lastWindowHeight !== this.windowHeight) {
                if (!suppressKeyboardChange && !this.isApp && this.isMobile && this.lastWindowWidth === this.windowWidth) {

                    // Issue with opening new tabs on IOS safari and chrome:
                    // Depending on the timing of opening the app, the initial window size reported may be the full screen size,
                    // which is shortly thereafter changed to reflect the actual available window size taking into account browser
                    // headers and footers.
                    // This can cause a false positive for detecting a keyboard open as the window height reduces.
                    // To solve this, if the initial window dimensions match the full screen size, we don't attempt to infer
                    // that the keyboard has opened.

                    if (Math.max(this.lastWindowWidth, this.lastWindowHeight) === Math.max(window.screen.width, window.screen.height) &&
                        Math.min(this.lastWindowWidth, this.lastWindowHeight) === Math.min(window.screen.width, window.screen.height)) {
                        console.log("Previous window size matched full screen - not inferring keyboard transition");
                    } else {
                        if (this.detectMobileBrowserKeyboardOpenClose(deferOnKeyboardChange)) {
                            console.log("Detected potential keyboard open/close - deferring action");
                            setTimeout(() => this.handleWindowResize(false, false), 250);
                            return;
                        }
                    }
                }

                notifyHeight = true;
                this.lastWindowHeight = this.windowHeight;
            }

            if (this.lastWindowWidth !== this.windowWidth) {
                notifyWidth = true;
                this.lastWindowWidth = this.windowWidth;
            }

            this.manageOrientation();
            this.manageMode();

            if (notifyHeight) {
                this._windowHeight$.next(window);
            }
            if (notifyWidth) {
                this._windowWidth$.next(window);
            }
        }

        document.documentElement.style.height = "100%";
        document.documentElement.style.maxHeight = "100%";

        /*
                // On iOS, webkit handles the reduction of height when the keyboard is open - if we adjust here, we get extra blank space above the keyboard.
                if (!(this.isIos && this.isKeyboardOpen)) {
                    setTimeout(() => {
                        const height: string = this.windowHeight + "px";
                        console.log("Changing document element height to " + height + " to match window/viewport height");
                        document.documentElement.style.height = height;
                        document.documentElement.style.maxHeight = height;

                        // Android attempts to scroll the html element and creates an overscroll condition
                        if (this.isAndroid) {
                            document.documentElement.scrollIntoView(true);
                            // @ts-ignore
                            if (document.activeElement.scrollIntoViewIfNeeded) {
                                // @ts-ignore
                                document.activeElement.scrollIntoViewIfNeeded(true);
                            } else {
                                document.activeElement.scrollIntoView({block: "center"});
                            }
                        }
                    }, 500);
                }
        */
    }

    /**
     * Detect virtual keyboard open/close for mobile browser (native events used for apps)
     * @private
     */
    private detectMobileBrowserKeyboardOpenClose(defer: boolean): boolean {
        const diff = this.windowHeight - this.lastWindowHeight;
        if (diff < -150) {
            if (defer) {
                return true;
            }
            this.notifyKeyboardOpen();
        }
        if (diff > 150) {
            if (defer) {
                return true;
            }
            this.notifyKeyboardClose();
        }
        return false;
    }

    private notifyKeyboardOpen(keyboardHeight: number = this.lastWindowHeight - this.windowHeight, emulateIos: boolean = false): void {
        if (keyboardHeight <= 0) {
            return;
        }

        if (!this.isKeyboardOpen) {
            console.log("Keyboard is open at height: " + keyboardHeight);
            this.isKeyboardOpen = true;
            this._keyboard$.next(this.isKeyboardOpen);
            document.body.classList.add("keyboard-is-open");

            // NOTE: Since we have switched to WKWebView, the following should not be called for an app on iOS, since we no longer listen to the keyboard open/close events.
            // Keeping this code in case of future issues in this area.
            //
            // iOS does not natively resize the webview when the keyboard opens.
            // cordova-plugin-ionic-keyboard would do this but is not compatible with UIWebView.
            // cordova-plugin-keyboard does work to some degree but produces inconsistent results, particularly on the desktop pages.
            // As such, we attempt to handle the iOS case ourselves by resizing the document element (<html>).
            if (((this.isApp && this.isIos) || emulateIos) && !this.isDocumentElementReducedForKeyboard) {
                // On iOS, window.innerHeight is reduced by the keyboard for some devices but not others
                let height: string;
                if (this.windowHeight === this.lastWindowHeight) {
                    height = (this.windowHeight - keyboardHeight) + "px";
                } else {
                    height = this.windowHeight + "px";
                }

                console.log("Changing document element height to " + height + " for IOS native app keyboard handling (deprecated)");
                document.documentElement.style.height = height;
                this.isDocumentElementReducedForKeyboard = true;
                setTimeout(() => {
                    this.manageMode();
                    this._windowHeight$.next(window);
                });
            }
        }

        /*
                document.body.style.setProperty("--document-scroll-top", document.documentElement.scrollTop + "px");
                document.body.style.setProperty("--keyboard-open-window-height", this.windowHeight + "px");
        */

        // Make sure the element with focus is visible
        if (document.activeElement) {
            setTimeout(() => {
                if (!this.isKeyboardOpen) {
                    return;
                }
                this.scrollActiveElementIntoView();
            }, 500);
        }
    }

    private notifyKeyboardClose(): void {
        if (this.isKeyboardOpen) {
            console.log("Keyboard is closed");
            this.isKeyboardOpen = false;
            this._keyboard$.next(this.isKeyboardOpen);
            document.body.classList.remove("keyboard-is-open");
            document.body.style.removeProperty("--document-scroll-top");
            document.body.style.removeProperty("--keyboard-open-window-height");

            // Restore the size of the document element
            if (this.isDocumentElementReducedForKeyboard) {
                setTimeout(() => {
                    if (this.isKeyboardOpen) {
                        console.log("Keyboard is reopened - not restoring document element height");
                    } else {
                        console.log("Restoring document element height");
                        document.documentElement.style.removeProperty("height");
                        this.isDocumentElementReducedForKeyboard = false;
                        setTimeout(() => {
                            this.manageMode();
                            this._windowHeight$.next(window);
                        });
                    }
                }, 100);
            }
        }
    }

    public scrollActiveElementIntoView(scrollBlock: ScrollLogicalPosition = "center") {
        // @ts-ignore
        if (document.activeElement.scrollIntoViewIfNeeded) {
            // @ts-ignore
            document.activeElement.scrollIntoViewIfNeeded(true);
        } else {
            const dist: number = this.getDistanceOffScreen(document.activeElement);
            if (dist) {
                document.activeElement.scrollIntoView({
                    block: scrollBlock,
                    inline: "nearest",
                    behavior: "smooth"
                });
                setTimeout(() => (<HTMLElement>document.activeElement).focus(), 500);
            }
        }
    }

    public getDistanceOffScreen(el: Element): number {
        const rect = el.getBoundingClientRect();
        if (rect.top < 0) {
            return rect.top;
        }
        if (rect.bottom > this.availableHeight) {
            return rect.bottom - this.availableHeight;
        }
        return 0;
    }

    private manageOrientation(): void {
        if (this.isApp) {
            const screen: any = window.screen;
            if (this.lastWindowHeight >= 1000 || this.lastWindowWidth >= 1000) {
                console.log(this.lastWindowWidth + "x" + this.lastWindowHeight + " - Unlocking screen rotation");
                // noinspection JSUnresolvedReference
                screen.orientation.unlock();
            } else {
                console.log(this.lastWindowWidth + "x" + this.lastWindowHeight + " - Locking screen rotation");
                // noinspection JSUnresolvedReference
                screen.orientation.lock("portrait");
            }
        }
    }

    private manageMode(): void {
        const newMode: PageMode = this.calculateMode(this.windowWidth);
        const newShort: boolean = this.availableHeight < 768;
        if (this._mode !== newMode || this._short !== newShort) {
            this._mode = newMode;
            this._short = newShort;
            this.notifyMode();
        }
    }

    private notifyMode(): void {
        this._mode$.next(this._mode);
        this._mobile$.next(this.mobile);
        this._desktop$.next(this.desktop);
    }

    public recheckAvailableWidth(): void {
        this.notifyMode();
    }
}



// WEBPACK FOOTER //
// ./src/app/orchestration/responsive.service.ts